import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface EncounterFormDraft {
  assessment?: string;
  assigned_to?: string;
  chief_complaint?: string;
  current_medications?: string;
  encounter_date?: string;
  encounter_type?: 'initial' | 'follow-up' | 'urgent' | 'telehealth' | 'telephone';
  heart_rate?: string;
  height?: string;
  o2_saturation?: string;
  patient_id?: string;
  plan?: string;
  pressure?: string;
  temperature?: string;
  template_id?: string;
  /**
   * Optional human-readable title for the recording. Surfaced on the
   * generated document (additional_context.title) and the encounter
   * (clinical_context.title) when present.
   */
  title?: string;
  weight?: string;
}

export interface EncounterSessionActions {
  clearSession: () => void;
  setFormDraft: (draft: Partial<EncounterFormDraft>) => void;
  setOriginRoute: (route: string) => void;
  setRecordingMeta: (meta: Partial<RecordingMeta>) => void;
  setTemplateContext: (ctx: Record<string, string>) => void;
}

export interface EncounterSessionState {
  formDraft: EncounterFormDraft;
  originRoute: string | null;
  recordingMeta: RecordingMeta;
  templateContext: Record<string, string>;
}

export interface RecordingMeta {
  encounterId?: string;
  finalDuration?: number;
  /**
   * Set when auto-resume after an interruption fails terminally (budget
   * exhausted or permission revoked). Cleared on successful start / resume.
   */
  lastError?: string;
  pausedDuration: number;
  startedAt: number | null;
  status: RecordingStatus;
}

/**
 * Recording lifecycle. `interrupted` means the audio track ended unexpectedly
 * (typically an iOS phone-call audio-focus loss). The provider keeps the
 * captured chunks and auto-resumes when the page becomes visible again, so
 * `interrupted` is transient — distinct from user-initiated `paused`.
 */
export type RecordingStatus = 'idle' | 'recording' | 'paused' | 'interrupted' | 'stopped';

const initialRecordingMeta: RecordingMeta = {
  status: 'idle',
  startedAt: null,
  pausedDuration: 0,
  encounterId: undefined,
  finalDuration: undefined,
};

const initialState: EncounterSessionState = {
  formDraft: {},
  originRoute: null,
  templateContext: {},
  recordingMeta: initialRecordingMeta,
};

export const useEncounterSessionStore = create<EncounterSessionActions & EncounterSessionState>()(
  persist(
    (set) => ({
      ...initialState,

      clearSession: () => set(initialState),

      setOriginRoute: (route) => set({ originRoute: route }),

      setTemplateContext: (ctx) => set({ templateContext: ctx }),

      setFormDraft: (draft) =>
        set((state) => ({
          formDraft: { ...state.formDraft, ...draft },
        })),

      setRecordingMeta: (meta) =>
        set((state) => ({
          recordingMeta: { ...state.recordingMeta, ...meta },
        })),
    }),
    {
      name: 'encounter-session',
      onRehydrateStorage: () => (state) => {
        if (state && state.recordingMeta.status !== 'idle') {
          state.setRecordingMeta({
            status: 'idle',
            startedAt: null,
            pausedDuration: 0,
            lastError: undefined,
          });
        }
      },
    },
  ),
);
